import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  AppDispatch,
  RootState,
} from "../../../../store/store";

import useLoading from "../../../../hooks/useLoading";
import useApiErrorHandler from "../../../../hooks/useApiErrorHandler";
import useModals from "../../../../hooks/useModals";

import {
  updatePaymentSummary,
  resetPaymentDetails,
  resetReceivablePaymentForm,
} from "../store/receivablePayment.slice";

import ReceivablePaymentService from "../services/receivablePayment.service";
import { invalidateCashQueries } from '../../../../features/cash/invalidateCashQueries';

export const useApplyPayment = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const { handle } = useApiErrorHandler();
  const { openModal } = useModals();

  const {
    showLoadingIndicator,
    hideLoadingIndicator,
  } = useLoading();

  /**
   * 🔥 leer estado real desde redux
   */
  const {
    summary,
    paymentDate,
    notes,
    payments,
  } = useSelector(
    (state: RootState) => state.receivablePayment
  );

  /**
   * 💰 Aplicar pago
   */
  const applyPayment = async () => {
    try {
      showLoadingIndicator();

      const payload = {
        customerId: summary?.customerId,
        receivableId: summary?.id || null,
        paymentDate,
        notes,
        details: payments.map((p) => ({
          paymentMethodId: p.paymentMethodId,
          amount: Number(p.amount) || 0,
          reference: p.reference || null,
        })),
      };

      const response =
        await ReceivablePaymentService.create(payload);

      const {
        title,
        message,
        type,
        statusCode,
        data: paymentData,
      } = response;

      console.log("✅ pago registrado:", paymentData);

      /**
       * 🔥 modal de éxito
       */
      if (statusCode === 201) {
        openModal(type, {
          title,
          message,
        });
      }

      /**
       * 🔥 actualizar summary visual
       */
      if (paymentData) {
        // El backend puede haber creado un movimiento en efectivo. Invalidar caja
        // siempre es seguro, incluso cuando el módulo de Caja está deshabilitado.
        invalidateCashQueries(dispatch);
        dispatch(
          updatePaymentSummary({
            paidAmount: paymentData.paidAmount,
            balance: paymentData.balance,
            status: paymentData.status,
          })
        );

        /**
         * 🔥 limpiar notas + métodos de pago
         */
        dispatch(resetPaymentDetails());
      }

      /**
       * 🔥 si quedó totalmente pagado
       */
      if (paymentData?.status === "PAID") {
        dispatch(resetReceivablePaymentForm());

        navigate("/finance/cxc/client-balances");
      }

      return paymentData;
    } catch (err: any) {
      handle(err);
      return null;
    } finally {
      hideLoadingIndicator();
    }
  };

  /**
   * ❌ Cancelar proceso de pago
   *
   * - limpia formulario completo
   * - regresa a listado principal
   */
  const cancelPayment = () => {
    dispatch(resetReceivablePaymentForm());

    navigate("/finance/cxc/client-balances");
  };

  return {
    // state
    summary,
    paymentDate,
    notes,
    payments,

    // actions
    applyPayment,
    cancelPayment,
  };
};
